import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'

import { useOpenToolbarFile } from '#/entities/shipment'
import { formatDate } from '#/shared/lib/format'
import { cn } from '#/shared/lib/utils'
import { ChevronDownIcon } from '#/shared/ui/icons'
import { RichText } from '#/shared/ui/rich-text'

import type {
  FeedDetailTone,
  FeedEntryExpanded,
  FeedEntryFile,
  FeedEntryTone,
  SidebarFeedEntryData,
} from './sidebar-feed-entries'

const DAY_KEY_FORMAT = 'YYYY-MM-DD'

const TONE_CLASSES: Record<FeedEntryTone, string> = {
  neutral: 'bg-gray-100 text-gray-500',
  comms: 'bg-blue-50 text-blue-500',
  task: 'bg-amber-100 text-amber-500',
  payment: 'bg-green-75 text-green-750',
}

const DETAIL_TONE_CLASSES: Record<FeedDetailTone, string> = {
  default: 'text-gray-500',
  income: 'text-green-750',
  expense: 'text-orange-500',
}

interface FeedDayGroup {
  key: string
  label: string
  entries: SidebarFeedEntryData[]
}

function dayLabel(
  key: string,
  today: dayjs.Dayjs,
  labels: { today: string; yesterday: string },
): string {
  const date = dayjs(key)
  if (date.isSame(today, 'day')) return labels.today
  if (date.isSame(today.subtract(1, 'day'), 'day')) return labels.yesterday
  return formatDate(key)
}

function groupFeedEntriesByDay(
  entries: SidebarFeedEntryData[],
  labels: { today: string; yesterday: string },
): FeedDayGroup[] {
  const today = dayjs()
  const groups: FeedDayGroup[] = []
  for (const entry of entries) {
    const key = dayjs(entry.occurredAt).format(DAY_KEY_FORMAT)
    const last = groups.at(-1)
    if (last?.key === key) last.entries.push(entry)
    else
      groups.push({
        key,
        label: dayLabel(key, today, labels),
        entries: [entry],
      })
  }
  return groups
}

function EntryDetail({ entry }: { entry: SidebarFeedEntryData }) {
  if (!entry.detail) return null
  if (entry.detailRich) {
    return (
      <RichText
        html={entry.detail}
        className="text-sm break-words text-gray-500"
      />
    )
  }
  return (
    <p
      className={cn(
        'text-sm break-words',
        DETAIL_TONE_CLASSES[entry.detailTone],
      )}
    >
      {entry.detail}
    </p>
  )
}

function EntryTag({ label }: { label: string }) {
  return (
    <span className="flex shrink-0 items-center rounded-md bg-blue-100 px-1.5 py-0.5 text-2xs font-medium text-blue-500">
      {label}
    </span>
  )
}

function downloadFile(file: FeedEntryFile) {
  const anchor = document.createElement('a')
  anchor.href = file.url
  anchor.download = file.name
  anchor.rel = 'noopener noreferrer'
  anchor.click()
}

function ExpandedFileBlock({ file }: { file: FeedEntryFile }) {
  const { t } = useTranslation()
  const openFile = useOpenToolbarFile()
  return (
    <>
      <p className="text-xs font-medium break-words text-gray-900">
        {file.name}
      </p>
      {file.meta && <p className="text-xs text-warm-500">{file.meta}</p>}
      <div className="flex items-center gap-4 text-xs font-medium text-blue-500">
        <button
          type="button"
          className="cursor-pointer select-none"
          onClick={(event) => {
            event.stopPropagation()
            void openFile(file.id)
          }}
        >
          {t('detailSidebar.feed.fileView')}
        </button>
        <button
          type="button"
          className="cursor-pointer select-none"
          onClick={(event) => {
            event.stopPropagation()
            downloadFile(file)
          }}
        >
          {t('detailSidebar.feed.fileDownload')}
        </button>
      </div>
    </>
  )
}

function ExpandedCard({ expanded }: { expanded: FeedEntryExpanded }) {
  return (
    <div className="mt-1 flex w-full flex-col gap-2 rounded-lg border border-warm-100 bg-warm-25 p-3">
      {expanded.file && <ExpandedFileBlock file={expanded.file} />}
      {expanded.meta.map((row) => (
        <div key={row.label} className="flex items-start gap-2 text-xs">
          <span className="w-15 shrink-0 font-medium text-warm-500">
            {row.label}
          </span>
          <span
            className={cn(
              'min-w-0 flex-1 break-words',
              row.tone ? DETAIL_TONE_CLASSES[row.tone] : 'text-gray-900',
            )}
          >
            {row.value}
          </span>
        </div>
      ))}
      {expanded.note && (
        <>
          {expanded.meta.length > 0 && (
            <div className="h-px w-full bg-warm-100" />
          )}
          {expanded.noteRich ? (
            <RichText
              html={expanded.note}
              className="text-xs break-words text-gray-500"
            />
          ) : (
            <p className="text-xs break-words text-gray-500">{expanded.note}</p>
          )}
        </>
      )}
    </div>
  )
}

function useClampMeasure(expanded: boolean, dep: string, measure: () => void) {
  useLayoutEffect(() => {
    if (!expanded) measure()
  }, [measure, expanded, dep])

  useEffect(() => {
    const handler = () => {
      if (!expanded) measure()
    }
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [measure, expanded])
}

function useDetailOverflow(expanded: boolean, detail: string) {
  const detailRef = useRef<HTMLDivElement | null>(null)
  const [clamped, setClamped] = useState(false)

  const measure = useCallback(() => {
    const el = detailRef.current
    if (!el) return
    setClamped(el.scrollHeight > el.clientHeight + 1)
  }, [])

  useClampMeasure(expanded, detail, measure)

  return { detailRef, clamped }
}

function useTitleOverflow(expanded: boolean, title: string) {
  const titleRef = useRef<HTMLParagraphElement | null>(null)
  const [clamped, setClamped] = useState(false)

  const measure = useCallback(() => {
    const el = titleRef.current
    if (!el) return
    setClamped(el.scrollWidth > el.clientWidth + 1)
  }, [])

  useClampMeasure(expanded, title, measure)

  return { titleRef, clamped }
}

function EntryTop({
  entry,
  expanded,
  titleRef,
}: {
  entry: SidebarFeedEntryData
  expanded: boolean
  titleRef: React.RefObject<HTMLParagraphElement | null>
}) {
  return (
    <div className="flex items-start justify-between gap-2">
      <div className="flex min-w-0 items-center gap-1">
        <p
          ref={titleRef}
          className={cn('text-sm text-gray-900', !expanded && 'truncate')}
        >
          <span className="font-medium">{entry.actor}</span>
          <span>{` ${entry.action}`}</span>
        </p>
        {entry.tag && <EntryTag label={entry.tag} />}
      </div>
      <span className="shrink-0 text-xs text-gray-300">{entry.time}</span>
    </div>
  )
}

function EntryChevron({ expanded }: { expanded: boolean }) {
  return (
    <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center self-start text-gray-500">
      <ChevronDownIcon
        className={cn(
          'h-1.25 w-2.5 transition-transform',
          expanded && 'rotate-180',
        )}
      />
    </span>
  )
}

function ShowMoreToggle({ expanded }: { expanded: boolean }) {
  const { t } = useTranslation()
  return (
    <span className="pt-1 text-xs font-medium text-blue-500 select-none">
      {t(
        expanded
          ? 'detailSidebar.feed.showLess'
          : 'detailSidebar.feed.showMore',
      )}
    </span>
  )
}

function EntryBody({
  entry,
  expanded,
  showTextToggle,
  detailRef,
  titleRef,
}: {
  entry: SidebarFeedEntryData
  expanded: boolean
  showTextToggle: boolean
  detailRef: React.RefObject<HTMLDivElement | null>
  titleRef: React.RefObject<HTMLParagraphElement | null>
}) {
  return (
    <div
      className={cn(
        'flex min-w-0 flex-1 flex-col gap-1',
        !entry.detail && 'min-h-8 justify-center',
      )}
    >
      <EntryTop entry={entry} expanded={expanded} titleRef={titleRef} />
      <div ref={detailRef} className={cn(!expanded && 'line-clamp-2')}>
        <EntryDetail entry={entry} />
      </div>
      {expanded && entry.expanded && (
        <div className="overflow-hidden">
          <div className="animate-in fade-in slide-in-from-top duration-300 ease-out">
            <ExpandedCard expanded={entry.expanded} />
          </div>
        </div>
      )}
      {showTextToggle && <ShowMoreToggle expanded={expanded} />}
    </div>
  )
}

function SidebarFeedEntry({ entry }: { entry: SidebarFeedEntryData }) {
  const [expanded, setExpanded] = useState(false)
  const { detailRef, clamped } = useDetailOverflow(expanded, entry.detail)
  const { titleRef, clamped: titleClamped } = useTitleOverflow(
    expanded,
    `${entry.actor} ${entry.action}`,
  )
  const expandable =
    clamped || titleClamped || expanded || Boolean(entry.expanded)
  const showTextToggle = !entry.expanded && (clamped || expanded)
  const Icon = entry.icon
  return (
    <div
      onClick={expandable ? () => setExpanded((prev) => !prev) : undefined}
      className={cn(
        'flex items-start gap-3 border-b border-gray-200 py-3.5 last:border-b-0',
        expandable && 'cursor-pointer select-none',
      )}
    >
      <div
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-lg',
          TONE_CLASSES[entry.tone],
        )}
      >
        <Icon className="size-4" />
      </div>
      <EntryBody
        entry={entry}
        expanded={expanded}
        showTextToggle={showTextToggle}
        detailRef={detailRef}
        titleRef={titleRef}
      />
      {expandable && <EntryChevron expanded={expanded} />}
    </div>
  )
}

export function SidebarFeedList({
  entries,
}: {
  entries: SidebarFeedEntryData[]
}) {
  const { t } = useTranslation()
  const groups = groupFeedEntriesByDay(entries, {
    today: t('detailSidebar.feed.today'),
    yesterday: t('detailSidebar.feed.yesterday'),
  })
  return (
    <div className="flex flex-col">
      {groups.map((group) => (
        <div key={group.key} className="flex flex-col">
          <div className="flex items-center pt-4 pb-3">
            <span className="text-xs font-medium text-gray-300 uppercase select-none">
              {group.label}
            </span>
          </div>
          {group.entries.map((entry) => (
            <SidebarFeedEntry key={`${entry.kind}:${entry.id}`} entry={entry} />
          ))}
        </div>
      ))}
    </div>
  )
}
